/**
 * 流星：每隔 5–14 秒在视野上方斜着划过一颗（做法和参数同原型）。
 * 放在离镜头 700 的地方：比星海远、比背景天幕近；朝向镜头的长条，头亮尾暗，0.9–1.5 秒走完。
 * 入场时、看照片时、减少动态效果时不出现（由 galaxy.ts 决定能不能放）。
 */
import * as THREE from 'three';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform float uAlpha;
varying vec2 vUv;
void main() {
  // uv.x 从尾到头，头最亮；uv.y 横过流星，中间最亮
  float head = vUv.x, core = 1.0 - abs(vUv.y - 0.5) * 2.0;
  gl_FragColor = vec4(vec3(1.0, 0.92, 0.85) * (1.0 + 2.0 * pow(head, 12.0)), pow(head, 2.2) * pow(core, 1.5) * uAlpha);
  #include <colorspace_fragment>
}
`;

interface Meteor {
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  on: boolean;
  t: number;
  life: number;
  len: number;
  p0: THREE.Vector3;
  dir: THREE.Vector3;
}

export class Meteors {
  readonly group = new THREE.Group();
  private readonly list: Meteor[];
  /** 下一颗的时间（秒，按星海的时钟） */
  private next = 7;
  private readonly x = new THREE.Vector3();
  private readonly y = new THREE.Vector3();
  private readonly z = new THREE.Vector3();
  private readonly p = new THREE.Vector3();
  private readonly basis = new THREE.Matrix4();

  constructor() {
    this.list = Array.from({ length: 2 }, () => {
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.ShaderMaterial({ uniforms: { uAlpha: { value: 0 } }, vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
      );
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.renderOrder = 3;
      this.group.add(mesh);
      return { mesh, on: false, t: 0, life: 1, len: 100, p0: new THREE.Vector3(), dir: new THREE.Vector3() };
    });
  }

  private spawn(camera: THREE.PerspectiveCamera): void {
    const m = this.list.find((x) => !x.on);
    if (!m) return;
    // 视野上半部分随便一个方向，离镜头 700
    const ray = this.p.set(Math.random() * 1.4 - 0.7, 0.3 + Math.random() * 0.55, 0.5).unproject(camera).sub(camera.position).normalize();
    m.p0.copy(camera.position).addScaledVector(ray, 700);
    const right = this.x.setFromMatrixColumn(camera.matrixWorld, 0), up = this.y.setFromMatrixColumn(camera.matrixWorld, 1);
    m.dir.copy(right).multiplyScalar(Math.random() < 0.5 ? -0.85 : 0.85).addScaledVector(up, -0.5).normalize();
    m.len = 70 + Math.random() * 60;
    m.life = 0.9 + Math.random() * 0.6;
    m.t = 0;
    m.on = true;
    m.mesh.visible = true;
  }

  /** 每帧调；allowed 为假时不放新的，已经在划的照样划完 */
  update(dt: number, time: number, camera: THREE.PerspectiveCamera, allowed: boolean): void {
    if (!allowed) this.next = Math.max(this.next, time + 3);
    else if (time > this.next) {
      this.next = time + 5 + Math.random() * 9;
      this.spawn(camera);
    }
    for (const m of this.list) {
      if (!m.on) continue;
      m.t += dt;
      const k = m.t / m.life;
      if (k >= 1) {
        m.on = false;
        m.mesh.visible = false;
        continue;
      }
      // 头部的位置；长条朝向镜头，沿着划过的方向
      const head = this.p.copy(m.p0).addScaledVector(m.dir, m.len * 1.6 * k);
      this.z.copy(camera.position).sub(head).normalize();
      this.x.copy(m.dir);
      this.y.crossVectors(this.z, this.x).normalize();
      this.z.crossVectors(this.x, this.y);
      m.mesh.quaternion.setFromRotationMatrix(this.basis.makeBasis(this.x, this.y, this.z));
      m.mesh.position.copy(head).addScaledVector(m.dir, -m.len / 2);
      m.mesh.scale.set(m.len, 1.4, 1);
      m.mesh.material.uniforms.uAlpha!.value = Math.sin(k * Math.PI);
    }
  }
}
